/**
 * Voices section for SessionDetail Participants tab.
 * Invite Voices for context (not votes); seat cap; auto-generate perspective on invite.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { Mic2, Plus, Trash2, Sparkles, AlertCircle, Loader2, Search } from 'lucide-react';
import {
  useAgentsAsUsersEnabled,
  VoiceBadge,
  useVoicesStore,
  voiceSeatCap,
  isVoiceInvitable,
} from '../../../features/voices';
import { runVoicePerspective } from '../../../features/voices/voiceAdvisoryService';
import { runVoiceEstimate } from '../../../features/voices/voiceEstimatorService';
import { useVoicesCountedEnabled } from '../hooks/useVoicesCountedEnabled';
import type { SessionVoiceSeat } from '../types';
import { useToolSessionStore } from '../../../stores/prioritization/toolSessionStore';
import { useEntityCommentsStore } from '../../../stores/prioritization/entityCommentsStore';
import { getItemCountState } from '../../../components/collection/types';
import type { PrioritizationSession } from '../../../types/prioritization/session';
import type { WorkspaceVoice } from '../types';

interface SessionVoicesPanelProps {
  session: PrioritizationSession;
  humanRosterSize: number;
  userId: string;
  itemInputs: Array<{ id: string; title: string; description?: string }>;
  /** When true, hide the standalone "Add Voice" UI (parent owns unified picker). */
  hideAddControls?: boolean;
  /** Expose invite handler to parent for unified people+Voices picker. */
  onReady?: (api: SessionVoicesPanelApi) => void;
}

export interface SessionVoicesPanelApi {
  inviteVoice: (voiceId: string, opts?: { overrideCap?: boolean; overrideReason?: string | null }) => Promise<boolean>;
  availableVoices: WorkspaceVoice[];
  seatsCount: number;
  cap: number;
  needsOverride: boolean;
  generatingIds: Set<string>;
  genErrors: Record<string, string>;
}

function resolveToolSessionAndItems(
  session: PrioritizationSession,
  itemInputs: Array<{ id: string; title: string; description?: string }>,
) {
  const toolSessions = useToolSessionStore.getState().toolSessions.filter(
    (ts) => ts.sessionId === session.id,
  );
  const toolSession = toolSessions[0] ?? null;
  const items =
    itemInputs.length > 0
      ? itemInputs
      : (toolSession?.items ?? []).map((it) => ({
          id: it.id,
          title: it.title,
          description: it.description,
        }));
  return { toolSession, items };
}

async function postDiscussionPerspective(
  session: PrioritizationSession,
  voice: WorkspaceVoice,
  summary: string,
  kind: 'constituency' | 'advocate' | 'counted_estimate' = 'constituency',
) {
  if (!session.workspaceId) return;
  const heading =
    kind === 'counted_estimate'
      ? 'Counted estimate'
      : kind === 'advocate'
        ? "Devil's advocate challenge"
        : 'Constituency perspective';
  const footer =
    kind === 'counted_estimate'
      ? '— Counted Voice input: included in the counted aggregate (badged). Results still pass human acceptance and ratification.'
      : '— Context only; not a vote. Corpus-grounded commentary for facilitators.';
  const body = [`[Voice · ${voice.displayName}] ${heading}`, '', summary, '', footer].join('\n');

  // author_id must be a real auth.users id (FK). Use the sponsor; durable Voice
  // attribution lives on author_principal_type + voice_id + creation_provenance.
  useEntityCommentsStore.getState().addComment({
    entityType: 'session',
    entityId: session.id,
    workspaceId: session.workspaceId,
    body,
    authorId: voice.sponsorUserId,
    authorName: `${voice.displayName} (Voice)`,
    authorPrincipalType: 'voice',
    voiceId: voice.id,
    creationSource: 'ai',
    creationReviewStatus: 'none',
    creationProvenance: 'ai_draft',
  });
}

export function SessionVoicesPanel({
  session,
  humanRosterSize,
  userId,
  itemInputs,
  hideAddControls = false,
  onReady,
}: SessionVoicesPanelProps) {
  const enabled = useAgentsAsUsersEnabled();
  const countedEnabled = useVoicesCountedEnabled();
  const voices = useVoicesStore((s) => s.voices);
  const fetchVoices = useVoicesStore((s) => s.fetchVoices);
  const fetchSeats = useVoicesStore((s) => s.fetchSeats);
  const addSeat = useVoicesStore((s) => s.addSeat);
  const removeSeat = useVoicesStore((s) => s.removeSeat);
  const setSeatPolicy = useVoicesStore((s) => s.setSeatPolicy);
  const getActiveSeats = useVoicesStore((s) => s.getActiveSeats);
  const storeError = useVoicesStore((s) => s.error);
  // PRD §4: the Counted opt-in is the facilitator's deliberate act.
  const isFacilitator = session.facilitatorId === userId;

  const [showAdd, setShowAdd] = useState(false);
  const [selectedVoiceId, setSelectedVoiceId] = useState('');
  const [voiceSearch, setVoiceSearch] = useState('');
  const [overrideCap, setOverrideCap] = useState(false);
  const [overrideReason, setOverrideReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [generatingIds, setGeneratingIds] = useState<Set<string>>(new Set());
  const [genErrors, setGenErrors] = useState<Record<string, string>>({});
  const [genMessage, setGenMessage] = useState<string | null>(null);

  const seats = getActiveSeats(session.id);
  const cap = voiceSeatCap(humanRosterSize);

  useEffect(() => {
    if (!enabled || !session.workspaceId) return;
    void fetchVoices(session.workspaceId);
    void fetchSeats(session.id);
  }, [enabled, session.workspaceId, session.id, fetchVoices, fetchSeats]);

  const seatedIds = useMemo(() => new Set(seats.map((s) => s.voiceId)), [seats]);

  const availableVoices = useMemo(
    () => voices.filter((v) => isVoiceInvitable(v) && !seatedIds.has(v.id)),
    [voices, seatedIds],
  );

  const filteredAvailable = useMemo(() => {
    const q = voiceSearch.trim().toLowerCase();
    if (!q) return availableVoices;
    return availableVoices.filter(
      (v) =>
        v.displayName.toLowerCase().includes(q) ||
        (v.corpusKind ?? '').toLowerCase().includes(q) ||
        (v.description ?? '').toLowerCase().includes(q),
    );
  }, [availableVoices, voiceSearch]);

  const availableCountState = getItemCountState(availableVoices.length);

  const voiceById = useMemo(() => {
    const m = new Map(voices.map((v) => [v.id, v]));
    return m;
  }, [voices]);

  const needsOverride = seats.length >= cap;

  const runGenerate = async (voice: WorkspaceVoice, opts?: { silent?: boolean }) => {
    if (!session.workspaceId) return;
    setGeneratingIds((prev) => new Set(prev).add(voice.id));
    setGenErrors((prev) => {
      const next = { ...prev };
      delete next[voice.id];
      return next;
    });
    if (!opts?.silent) setGenMessage(null);

    const { toolSession, items } = resolveToolSessionAndItems(session, itemInputs);
    if (!toolSession) {
      const msg = 'No tool session yet — start the async run, then regenerate perspective.';
      setGenErrors((prev) => ({ ...prev, [voice.id]: msg }));
      if (!opts?.silent) setGenMessage(msg);
      setGeneratingIds((prev) => {
        const next = new Set(prev);
        next.delete(voice.id);
        return next;
      });
      return;
    }
    if (items.length === 0) {
      const msg = 'No items on the session yet — add items, then regenerate.';
      setGenErrors((prev) => ({ ...prev, [voice.id]: msg }));
      if (!opts?.silent) setGenMessage(msg);
      setGeneratingIds((prev) => {
        const next = new Set(prev);
        next.delete(voice.id);
        return next;
      });
      return;
    }

    // Seat-aware generation (Phase 2): counted estimator seats produce
    // numeric estimates; advocate seats argue against the consensus;
    // everything else (and every advisory seat) stays constituency text.
    const seat = getActiveSeats(session.id).find((s) => s.voiceId === voice.id);
    const isCountedEstimator =
      countedEnabled && seat?.seatType === 'estimator' && seat.countingPolicy === 'counted';

    let result: { summary: string; error: string | null };
    let kind: 'constituency' | 'advocate' | 'counted_estimate' = 'constituency';
    if (isCountedEstimator && seat) {
      kind = 'counted_estimate';
      const est = await runVoiceEstimate({
        voice,
        seat,
        toolSessionId: toolSession.id,
        workspaceId: session.workspaceId,
        items,
        facilitatorId: session.facilitatorId,
      });
      result = { summary: est.summary, error: est.error };
    } else {
      if (seat?.seatType === 'advocate') kind = 'advocate';
      result = await runVoicePerspective({
        voice,
        toolSessionId: toolSession.id,
        workspaceId: session.workspaceId,
        items,
        facilitatorId: session.facilitatorId,
        seatType: kind === 'advocate' ? 'advocate' : 'constituency',
      });
    }

    if (result.error) {
      setGenErrors((prev) => ({ ...prev, [voice.id]: result.error! }));
      if (!opts?.silent) setGenMessage(result.error);
    } else {
      await useToolSessionStore.getState().subscribeToToolSession(toolSession.id);
      await postDiscussionPerspective(session, voice, result.summary, kind);
      if (!opts?.silent) {
        setGenMessage(
          kind === 'counted_estimate'
            ? `${voice.displayName} estimates saved — counted in the aggregate (badged); results still require human acceptance.`
            : `${voice.displayName} perspective saved — context only, not counted in votes.`,
        );
      }
    }

    setGeneratingIds((prev) => {
      const next = new Set(prev);
      next.delete(voice.id);
      return next;
    });
  };

  // PRD §4 facilitator opt-in: advisory ↔ counted, attributed. Seat policy is
  // session configuration (which lane future contributions land in) — already
  // -persisted responses keep their audit-stable snapshot.
  const handlePolicyChange = async (seat: SessionVoiceSeat, policy: 'advisory' | 'counted') => {
    if (!isFacilitator || !countedEnabled || seat.countingPolicy === policy) return;
    const ok = await setSeatPolicy(session.id, seat.id, policy, userId);
    if (ok) {
      const voiceName = voiceById.get(seat.voiceId)?.displayName ?? 'Voice';
      setGenMessage(
        policy === 'counted'
          ? `${voiceName} seat set to Counted — its future estimator output joins the aggregate, badged. Regenerate to produce counted estimates.`
          : `${voiceName} seat back to Advisory — future contributions are context only.`,
      );
    }
  };

  const inviteVoice = async (
    voiceId: string,
    opts?: { overrideCap?: boolean; overrideReason?: string | null },
  ): Promise<boolean> => {
    setError(null);
    const useOverride = opts?.overrideCap ?? false;
    if (needsOverride && !useOverride) {
      setError(
        `Invite cap is ${cap} Voice${cap === 1 ? '' : 's'} (min of 2 or 20% of roster). Check override and provide a reason.`,
      );
      return false;
    }
    if (useOverride && !(opts?.overrideReason ?? '').trim()) {
      setError('Override requires a logged reason.');
      return false;
    }
    // Seat type follows the Voice's configured participation seats
    // (Settings → Voices): constituency preferred, else the first eligible
    // seat (estimator / advocate). Counting policy always starts advisory.
    const invitedVoice = voiceById.get(voiceId) ?? voices.find((v) => v.id === voiceId);
    const seatType = invitedVoice?.participationSeats.includes('constituency')
      ? 'constituency'
      : (invitedVoice?.participationSeats[0] ?? 'constituency');
    const result = await addSeat({
      sessionId: session.id,
      voiceId,
      addedBy: userId,
      seatType,
      overrideCap: needsOverride ? useOverride : false,
      overrideReason: needsOverride && useOverride ? (opts?.overrideReason ?? '').trim() : null,
    });
    if (!result) {
      setError(storeError ?? 'Failed to invite Voice.');
      return false;
    }
    const voice = voiceById.get(voiceId) ?? voices.find((v) => v.id === voiceId);
    if (voice) {
      void runGenerate(voice, { silent: true });
    }
    return true;
  };

  const onReadyRef = useRef(onReady);
  onReadyRef.current = onReady;

  useEffect(() => {
    if (!enabled || !onReadyRef.current) return;
    onReadyRef.current({
      inviteVoice,
      availableVoices,
      seatsCount: seats.length,
      cap,
      needsOverride,
      generatingIds,
      genErrors,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- expose latest API snapshot; inviteVoice closes over current state
  }, [enabled, availableVoices, seats.length, cap, needsOverride, generatingIds, genErrors]);

  if (!enabled) return null;

  const handleAdd = async () => {
    if (!selectedVoiceId) return;
    const ok = await inviteVoice(selectedVoiceId, {
      overrideCap,
      overrideReason: overrideReason.trim() || null,
    });
    if (!ok) return;
    setShowAdd(false);
    setSelectedVoiceId('');
    setOverrideCap(false);
    setOverrideReason('');
    setVoiceSearch('');
  };

  return (
    <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-6">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="text-sm font-semibold text-[var(--text-pri)] flex items-center gap-2">
            Voices
            <VoiceBadge />
          </h3>
          <p className="text-xs text-[var(--text-ter)] mt-0.5">
            Context providers · invite cap {cap} (of {humanRosterSize} humans) · never counted as votes
          </p>
        </div>
        {!hideAddControls && (
          <button
            type="button"
            onClick={() => setShowAdd(true)}
            className="btn btn-secondary btn-sm flex items-center gap-1"
            disabled={availableVoices.length === 0 && !showAdd}
          >
            <Plus className="h-4 w-4" />
            Invite Voice
          </button>
        )}
      </div>

      {genMessage && (
        <p className="mb-3 text-xs text-primary-700 bg-primary-50 px-3 py-2">{genMessage}</p>
      )}

      {!hideAddControls && showAdd && (
        <div className="mb-4 space-y-3 p-3 border-2 border-electric-mustard-200 bg-electric-mustard-50/40">
          {availableCountState === 'many' && (
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[var(--text-ter)]" />
              <input
                type="search"
                value={voiceSearch}
                onChange={(e) => setVoiceSearch(e.target.value)}
                placeholder="Search Voices…"
                className="w-full border border-[var(--rule)] pl-8 pr-3 py-2 text-sm bg-[var(--bg-raised)]"
              />
            </div>
          )}
          <select
            value={selectedVoiceId}
            onChange={(e) => {
              setSelectedVoiceId(e.target.value);
              setError(null);
            }}
            className="w-full border border-[var(--rule)] px-3 py-2 text-sm bg-[var(--bg-raised)]"
          >
            <option value="">Select a Voice…</option>
            {filteredAvailable.map((v) => (
              <option key={v.id} value={v.id}>
                {v.displayName} ({v.corpusKind ?? 'no corpus'})
              </option>
            ))}
          </select>
          {needsOverride && (
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-xs text-[var(--text-sec)]">
                <input
                  type="checkbox"
                  checked={overrideCap}
                  onChange={(e) => setOverrideCap(e.target.checked)}
                />
                Override invite cap (logged deliberate action)
              </label>
              {overrideCap && (
                <input
                  type="text"
                  value={overrideReason}
                  onChange={(e) => setOverrideReason(e.target.value)}
                  placeholder="Reason for override…"
                  className="w-full border border-[var(--rule)] px-3 py-2 text-sm"
                />
              )}
            </div>
          )}
          {error && (
            <p className="text-xs text-[var(--coral)] flex items-center gap-1">
              <AlertCircle className="h-3 w-3 flex-shrink-0" />
              {error}
            </p>
          )}
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => void handleAdd()}
              disabled={!selectedVoiceId}
              className="px-3 py-2 bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50 text-sm"
            >
              Invite for context
            </button>
            <button
              type="button"
              onClick={() => {
                setShowAdd(false);
                setSelectedVoiceId('');
                setError(null);
                setOverrideCap(false);
                setOverrideReason('');
                setVoiceSearch('');
              }}
              className="px-3 py-2 border border-[var(--rule)] hover:bg-[var(--surface)] text-sm"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      <div className="border border-[var(--rule)] overflow-hidden">
        <table className="min-w-full divide-y divide-[var(--rule)]">
          <thead className="bg-[var(--surface)]">
            <tr>
              <th className="px-4 py-2 text-left text-xs font-medium text-[var(--text-ter)] uppercase">Name</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-[var(--text-ter)] uppercase">Status</th>
              {countedEnabled && (
                <th className="px-4 py-2 text-left text-xs font-medium text-[var(--text-ter)] uppercase">Policy</th>
              )}
              <th className="px-4 py-2 text-left text-xs font-medium text-[var(--text-ter)] uppercase">Actions</th>
              <th className="px-4 py-2 text-right text-xs font-medium text-[var(--text-ter)] uppercase" />
            </tr>
          </thead>
          <tbody className="divide-y divide-[var(--rule)]">
            {seats.map((seat) => {
              const voice = voiceById.get(seat.voiceId);
              const isGenerating = generatingIds.has(seat.voiceId);
              const rowError = genErrors[seat.voiceId];
              return (
                <tr key={seat.id} className="hover:bg-[var(--surface)]">
                  <td className="px-4 py-2 text-sm text-[var(--text-pri)]">
                    <span className="inline-flex items-center gap-2 flex-wrap">
                      <Mic2 className="h-4 w-4 text-electric-mustard-600" />
                      {voice?.displayName ?? seat.voiceId.slice(0, 8)}
                      <VoiceBadge label={voice?.badgeLabel ?? 'Voice'} />
                      {seat.overrideCap && (
                        <span className="text-[10px] uppercase tracking-wider font-mono text-coral-700">
                          cap override
                        </span>
                      )}
                      {/* Attribution non-optional: a counted seat is labeled even
                          when the counted UI flag is off for this viewer. */}
                      {seat.countingPolicy === 'counted' && (
                        <span className="text-[10px] uppercase tracking-wider font-mono text-[var(--green)] dark:text-[var(--text-ter)]">
                          counted
                        </span>
                      )}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-sm">
                    {isGenerating ? (
                      <span className="inline-flex items-center gap-1.5 text-xs text-primary-700">
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        Generating perspective…
                      </span>
                    ) : rowError ? (
                      <span className="inline-flex items-start gap-1 text-xs text-[var(--coral)] max-w-[220px]">
                        <AlertCircle className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
                        {rowError}
                      </span>
                    ) : (
                      <span className="text-xs text-[var(--text-ter)]">
                        {seat.seatType === 'estimator' && seat.countingPolicy === 'counted'
                          ? 'Counted estimator'
                          : seat.seatType === 'advocate'
                            ? "Devil's advocate (context)"
                            : 'Context provider'}
                      </span>
                    )}
                  </td>
                  {countedEnabled && (
                    <td className="px-4 py-2">
                      {isFacilitator ? (
                        <div
                          className="inline-flex border border-[var(--rule)] overflow-hidden"
                          role="group"
                          aria-label={`Counting policy for ${voice?.displayName ?? 'Voice'}`}
                        >
                          <button
                            type="button"
                            onClick={() => void handlePolicyChange(seat, 'advisory')}
                            className={`px-2 py-1 text-[11px] font-medium ${
                              seat.countingPolicy !== 'counted'
                                ? 'bg-[var(--bg-raised)] text-white'
                                : 'bg-[var(--bg-raised)] text-[var(--text-sec)] hover:bg-[var(--surface)]'
                            }`}
                          >
                            Advisory
                          </button>
                          <button
                            type="button"
                            onClick={() => void handlePolicyChange(seat, 'counted')}
                            className={`px-2 py-1 text-[11px] font-medium ${
                              seat.countingPolicy === 'counted'
                                ? 'bg-[var(--green)] text-white'
                                : 'bg-[var(--bg-raised)] text-[var(--text-sec)] hover:bg-[var(--surface)]'
                            }`}
                            title="Facilitator opt-in (logged): counted estimator output joins the counted aggregate, badged. Results still require human acceptance."
                          >
                            Counted
                          </button>
                        </div>
                      ) : (
                        <span className="text-xs text-[var(--text-ter)] capitalize">
                          {seat.countingPolicy === 'counted' ? 'Counted' : 'Advisory'}
                        </span>
                      )}
                    </td>
                  )}
                  <td className="px-4 py-2">
                    <button
                      type="button"
                      onClick={() => voice && void runGenerate(voice)}
                      disabled={isGenerating || !voice}
                      className="inline-flex items-center gap-1 text-xs font-medium text-primary-600 hover:text-primary-800 disabled:opacity-50"
                    >
                      <Sparkles className="h-3.5 w-3.5" />
                      {isGenerating ? 'Generating…' : 'Regenerate perspective'}
                    </button>
                  </td>
                  <td className="px-4 py-2 text-right">
                    <button
                      type="button"
                      onClick={() => void removeSeat(session.id, seat.id)}
                      className="text-[var(--coral)] hover:text-[var(--coral)]"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              );
            })}
            {seats.length === 0 && (
              <tr>
                <td colSpan={countedEnabled ? 5 : 4} className="px-4 py-4 text-sm text-[var(--text-ter)] text-center">
                  No Voices invited. Add a Customer or Revenue Voice for constituency context.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
